import React, { useState, useEffect } from 'react';
import { Play, Plus, Edit, Trash2, RefreshCw } from 'lucide-react';
import icalService from '../../../services/icalService';
import housesService from '../../../services/housesService';
import { ICal, House } from '../../../services/types';
import ICalModal from './ICalModal';
import './ical.css';

const ICalManagement = () => {
    const [icals, setICals] = useState<ICal[]>([]);
    const [houses, setHouses] = useState<House[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingICal, setEditingICal] = useState<ICal | null>(null);
    const [isRunning, setIsRunning] = useState<string | null>(null);

    useEffect(() => {
        fetchICals();
        fetchHouses();
    }, []);

    const fetchICals = async () => {
        try {
            setLoading(true);
            const data = await icalService.getAll();
            setICals(data);
        } catch (error) {
            console.error('Error fetching iCals:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchHouses = async () => {
        try {
            const data = await housesService.getAll();
            setHouses(data);
        } catch (error) {
            console.error('Error fetching houses:', error);
        }
    };

    const getHouseName = (houseId: string) => {
        const house = houses.find(h => h.public_id === houseId);
        return house?.name || house?.address || 'Unknown House';
    };

    const handleCreate = () => {
        setEditingICal(null);
        setIsModalOpen(true);
    };

    const handleEdit = (ical: ICal) => {
        setEditingICal(ical);
        setIsModalOpen(true);
    };

    const handleDelete = async (publicId: string) => {
        if (!window.confirm('Are you sure you want to delete this iCal?')) {
            return;
        }

        try {
            await icalService.delete(publicId);
            await fetchICals();
        } catch (error) {
            console.error('Error deleting iCal:', error);
            alert('Failed to delete iCal');
        }
    };

    const handleToggleActive = async (publicId: string, currentStatus: boolean) => {
        try {
            await icalService.toggleActive(publicId, !currentStatus);
            await fetchICals();
        } catch (error) {
            console.error('Error toggling iCal status:', error);
            alert('Failed to toggle iCal status');
        }
    };

    const handleRun = async (publicId: string) => {
        setIsRunning(publicId);
        try {
            const response = await icalService.run(publicId);
            alert(`iCal fetched successfully!\n\nData preview:\n${response.data?.substring(0, 200)}...`);
        } catch (error) {
            console.error('Error running iCal:', error);
            alert('Failed to run iCal');
        } finally {
            setIsRunning(null);
        }
    };

    const handleRunAll = async () => {
        if (!window.confirm('Are you sure you want to run all active iCals?')) {
            return;
        }

        setIsRunning('all');
        try {
            const response = await icalService.runAll();
            const results = Object.entries(response.results)
                .map(([name, status]) => `${name}: ${status}`)
                .join('\n');
            alert(`All iCals processed!\n\nResults:\n${results}`);
        } catch (error) {
            console.error('Error running all iCals:', error);
            alert('Failed to run all iCals');
        } finally {
            setIsRunning(null);
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingICal(null);
        fetchICals();
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="ical-management">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>iCal Management</h2>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-success"
                        onClick={handleRunAll}
                        disabled={isRunning === 'all'}
                    >
                        {isRunning === 'all' ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Running...
                            </>
                        ) : (
                            <>
                                <RefreshCw size={18} className="me-2" />
                                Run All Active
                            </>
                        )}
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleCreate}
                    >
                        <Plus size={18} className="me-2" />
                        Add New iCal
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>House</th>
                                    <th>URL</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {icals.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-4 text-muted">
                                            No iCals found. Create one to get started.
                                        </td>
                                    </tr>
                                ) : (
                                    icals.map((ical) => (
                                        <tr key={ical.public_id}>
                                            <td className="fw-bold">{ical.name}</td>
                                            <td>{getHouseName(ical.house_id)}</td>
                                            <td>
                                                <a
                                                    href={ical.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-truncate d-inline-block"
                                                    style={{ maxWidth: '300px' }}
                                                    title={ical.url}
                                                >
                                                    {ical.url}
                                                </a>
                                            </td>
                                            <td>
                                                <button
                                                    className={`btn btn-sm ${ical.active ? 'btn-success' : 'btn-secondary'}`}
                                                    onClick={() => handleToggleActive(ical.public_id!, ical.active)}
                                                >
                                                    {ical.active ? 'Active' : 'Inactive'}
                                                </button>
                                            </td>
                                            <td>
                                                {ical.created_at ? new Date(ical.created_at).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => handleRun(ical.public_id!)}
                                                        disabled={!ical.active || isRunning === ical.public_id}
                                                        title="Run iCal"
                                                    >
                                                        {isRunning === ical.public_id ? (
                                                            <span className="spinner-border spinner-border-sm" />
                                                        ) : (
                                                            <Play size={16} />
                                                        )}
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-warning"
                                                        onClick={() => handleEdit(ical)}
                                                        title="Edit"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleDelete(ical.public_id!)}
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <ICalModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    ical={editingICal}
                    houses={houses}
                />
            )}
        </div>
    );
};

export default ICalManagement;
